import { formatRupiah } from "@/lib/display-format";
import { useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, CheckCircle2, CopyCheck, Eye, FilePlus2, LoaderCircle, ShieldCheck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { DuplicateAnalysis, DuplicateReason, Language, Transaction } from "@/lib/finance";
import type { BusinessUnit } from "@/lib/business-units";
import type { FinanceCategory } from "@/lib/categories";
import type { FinancialAccount } from "@/lib/profit-loss";
import { TransactionDetailPreview } from "./transaction-detail-preview";

export type ImportReviewProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analysis: DuplicateAnalysis | null;
  language: Language;
  busy: boolean;
  onConfirm: (mergeTargets: Record<number, string>) => void;
  onCancel: () => void;
  mode?: "import" | "manual";
  units: BusinessUnit[];
  categories: FinanceCategory[];
  accounts: FinancialAccount[];
};

function money(value: number, language: Language) {
  return formatRupiah(value, language);
}

function shortTransaction(transaction: Transaction, language: Language) {
  return `${transaction.date} · ${transaction.direction === "CREDIT" ? (language === "id" ? "Kredit" : "Credit") : (language === "id" ? "Debet" : "Debit")} · ${money(transaction.amount, language)}`;
}

export function ImportReview({
  open,
  onOpenChange,
  analysis,
  language,
  busy,
  onConfirm,
  onCancel,
  mode = "import",
  units,
  categories,
  accounts,
}: ImportReviewProps) {
  const [decisions, setDecisions] = useState<Record<number, string | null>>({});
  const [candidateSelections, setCandidateSelections] = useState<Record<number, string>>({});
  const [detail, setDetail] = useState<{ transaction: Transaction; persisted: boolean } | null>(null);
  const text = language === "id" ? {
    title: mode === "manual" ? "Periksa input manual yang mirip" : "Periksa kemungkinan duplikat",
    description: mode === "manual"
      ? "Pilih simpan sebagai transaksi baru atau gabungkan ke baris lama. Gabungkan tidak membuat baris transaksi baru, tetapi keputusan dan isi input tetap dicatat dalam audit."
      : "Referensi nyata diperiksa pertama. Jika tidak tersedia, nama pengirim hanya memunculkan dugaan saat deskripsi, tipe Debet/Kredit, dan waktu transaksi juga sama.",
    exact: "Referensi sama",
    newRows: "Tanpa dugaan",
    review: "Perlu keputusan",
    incoming: mode === "manual" ? "Input manual baru" : "Baris dari file baru",
    existing: "Kandidat transaksi lama",
    keep: "Simpan sebagai transaksi baru",
    merge: "Gabungkan ke transaksi lama",
    candidate: "Pilih kandidat",
    finish: mode === "manual" ? "Simpan keputusan" : "Lanjutkan impor",
    cancel: mode === "manual" ? "Kembali ke formulir" : "Batalkan impor",
    safeDefault: "Pilihan aman bawaan adalah menyimpan sebagai transaksi baru.",
    matchLegend: "Bidang berwarna menandai kecocokan yang memicu dugaan duplikasi.",
    senderReason: "Nama pengirim sama",
    account: "Rekening atau penerima sama",
    descriptionReason: "Deskripsi serupa",
    dateTimeReason: "Tanggal atau waktu cocok",
    amountReason: "Nominal sama",
    directionReason: "Tipe transaksi sama",
    referenceReason: "No. referensi sama",
    reference: "No. referensi",
    senderField: "Nama pengirim",
    dateTime: "Tanggal/waktu",
    descriptionField: "Deskripsi",
    amount: "Nominal",
    direction: "Tipe",
    accountField: "Rekening/penerima",
    unavailable: "Tidak tersedia",
    viewDetails: "Lihat detail lengkap",
  } : {
    title: mode === "manual" ? "Review similar manual entry" : "Review possible duplicates",
    description: mode === "manual"
      ? "Save it as a new transaction or merge it into an existing row. Merge creates no new transaction row, but the decision and submitted entry remain in the audit trail."
      : "A real reference is checked first. When unavailable, a sender name only raises a match when description, Debit/Credit type, and transaction time also match.",
    exact: "Same reference",
    newRows: "No indication",
    review: "Needs a decision",
    incoming: mode === "manual" ? "New manual entry" : "Row from the new file",
    existing: "Existing transaction candidate",
    keep: "Save as a new transaction",
    merge: "Merge into existing transaction",
    candidate: "Choose candidate",
    finish: mode === "manual" ? "Save decision" : "Continue import",
    cancel: mode === "manual" ? "Back to form" : "Cancel import",
    safeDefault: "The safe default is to save it as a new transaction.",
    matchLegend: "Colored fields identify the matches that triggered the duplicate indication.",
    senderReason: "Same sender name",
    account: "Same account or recipient",
    descriptionReason: "Similar description",
    dateTimeReason: "Matching date/time",
    amountReason: "Same amount",
    directionReason: "Same transaction type",
    referenceReason: "Same reference number",
    reference: "Reference number",
    senderField: "Sender name",
    dateTime: "Date/time",
    descriptionField: "Description",
    amount: "Amount",
    direction: "Type",
    accountField: "Account/recipient",
    unavailable: "Unavailable",
    viewDetails: "View full details",
  };

  useEffect(() => {
    if (!open || !analysis) return;
    setDecisions(Object.fromEntries(analysis.reviewItems.map((item) => [item.incomingIndex, null])));
    setCandidateSelections(Object.fromEntries(analysis.reviewItems.map((item) => [item.incomingIndex, item.candidates[0].transaction.id])));
    setDetail(null);
  }, [open, analysis]);

  const mergeTargets = useMemo(() => Object.fromEntries(
    Object.entries(decisions).filter((entry): entry is [string, string] => typeof entry[1] === "string" && Boolean(entry[1])),
  ), [decisions]);

  const reasonLabel = (reason: DuplicateReason) => ({
    reference: text.referenceReason,
    sender: text.senderReason,
    dateTime: text.dateTimeReason,
    description: text.descriptionReason,
    amount: text.amountReason,
    direction: text.directionReason,
    account: text.account,
  })[reason];

  return <><Sheet open={open} onOpenChange={(next) => { if (!busy) { if (!next) setDetail(null); onOpenChange(next); } }}>
    <SheetContent className="w-full overflow-y-auto sm:max-w-4xl">
      <SheetHeader className="border-b px-4 py-5 sm:px-6">
        <SheetTitle className="flex items-center gap-2"><ShieldCheck className="size-5" />{text.title}</SheetTitle>
        <SheetDescription>{text.description}</SheetDescription>
      </SheetHeader>
      {analysis && <div className="space-y-5 p-4 sm:p-6">
        <div className="grid gap-3 sm:grid-cols-3">
          <Summary icon={<CopyCheck />} value={analysis.referenceMatchCount} label={text.exact} />
          <Summary icon={<FilePlus2 />} value={analysis.newTransactionCount} label={text.newRows} />
          <Summary icon={<ArrowRightLeft />} value={analysis.reviewItems.length} label={text.review} featured />
        </div>
        <div className="space-y-1 rounded-xl border bg-muted/25 p-4 text-sm text-muted-foreground"><p>{text.safeDefault}</p><p>{text.matchLegend}</p></div>

        <div className="space-y-4">
          {analysis.reviewItems.map((item, index) => {
            const selectedId = decisions[item.incomingIndex];
            const selectedCandidateId = candidateSelections[item.incomingIndex] || item.candidates[0].transaction.id;
            const selectedCandidate = item.candidates.find((candidate) => candidate.transaction.id === selectedCandidateId)
              || item.candidates[0];
            return <article key={`${item.incoming.fingerprint}-${item.incomingIndex}`} className="overflow-hidden rounded-2xl border bg-card">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/30 px-4 py-3">
                <p className="text-sm font-semibold">#{index + 1} · {shortTransaction(item.incoming, language)}</p>
                <Badge variant="outline" className="font-mono">{item.incoming.uniqueCode}</Badge>
              </div>
              <div className="grid gap-4 p-4 lg:grid-cols-2">
                <TransactionPreview label={text.incoming} transaction={item.incoming} language={language} reasons={selectedCandidate.reasons} text={text} onView={() => setDetail({ transaction: item.incoming, persisted: false })} />
                <div className="space-y-3 rounded-xl border border-dashed p-4">
                  <div className="flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{text.existing}</p>
                    {item.candidates.length > 1 && <Select value={selectedCandidate.transaction.id} onValueChange={(value) => { setCandidateSelections((current) => ({ ...current, [item.incomingIndex]: value })); if (selectedId) setDecisions((current) => ({ ...current, [item.incomingIndex]: value })); }}>
                      <SelectTrigger size="sm" className="w-full sm:w-[190px]"><SelectValue placeholder={text.candidate} /></SelectTrigger>
                      <SelectContent>{item.candidates.map((candidate) => <SelectItem key={candidate.transaction.id} value={candidate.transaction.id}>{candidate.transaction.date} · {candidate.transaction.paymentRef}</SelectItem>)}</SelectContent>
                    </Select>}
                  </div>
                  <TransactionPreview transaction={selectedCandidate.transaction} language={language} reasons={selectedCandidate.reasons} text={text} onView={() => setDetail({ transaction: selectedCandidate.transaction, persisted: true })} />
                  <div className="flex flex-wrap gap-1.5">{selectedCandidate.reasons.map((reason) => <Badge key={reason} variant="outline" className="rounded-full border-amber-500/35 bg-amber-500/8 text-[11px] text-amber-800 dark:text-amber-200">{reasonLabel(reason)}</Badge>)}</div>
                </div>
              </div>
              <div className="grid gap-2 border-t p-4 sm:grid-cols-2">
                <Button type="button" className="min-h-11" variant={!selectedId ? "default" : "outline"} onClick={() => setDecisions((current) => ({ ...current, [item.incomingIndex]: null }))}><FilePlus2 />{text.keep}</Button>
                <Button type="button" className="min-h-11" variant={selectedId ? "default" : "outline"} onClick={() => setDecisions((current) => ({ ...current, [item.incomingIndex]: selectedCandidate.transaction.id }))}><ArrowRightLeft />{text.merge}</Button>
              </div>
            </article>;
          })}
        </div>

        <div className="sticky bottom-0 grid gap-2 rounded-2xl border bg-background/95 p-3 shadow-lg backdrop-blur sm:grid-cols-[1fr_2fr]">
          <Button variant="outline" className="min-h-11" disabled={busy} onClick={onCancel}>{text.cancel}</Button>
          <Button className="min-h-11" disabled={busy} onClick={() => onConfirm(Object.fromEntries(Object.entries(mergeTargets).map(([key, value]) => [Number(key), value])))}>{busy ? <LoaderCircle className="animate-spin" /> : <CheckCircle2 />}{text.finish}</Button>
        </div>
      </div>}
    </SheetContent>
  </Sheet><TransactionDetailPreview transaction={detail?.transaction || null} onOpenChange={(next) => { if (!next) setDetail(null); }} language={language} units={units} categories={categories} accounts={accounts} persisted={detail?.persisted ?? false} /></>;
}

function Summary({ icon, value, label, featured = false }: { icon: React.ReactNode; value: number; label: string; featured?: boolean }) {
  return <div className={`rounded-2xl border p-4 ${featured ? "border-amber-500/30 bg-amber-500/8" : "bg-card"}`}><span className={`mb-3 flex size-9 items-center justify-center rounded-xl [&_svg]:size-4 ${featured ? "bg-amber-500/15 text-amber-700 dark:text-amber-300" : "bg-muted text-primary"}`}>{icon}</span><p className="text-2xl font-semibold tabular-nums">{value}</p><p className="mt-1 text-sm text-muted-foreground">{label}</p></div>;
}

export type ReviewText = {
  reference: string;
  senderField: string;
  dateTime: string;
  descriptionField: string;
  amount: string;
  direction: string;
  accountField: string;
  unavailable: string;
  viewDetails: string;
  beneficiaryBank?: string;
  ftNumber?: string;
};

function accountValue(transaction: Transaction, unavailable: string, includeBank = true) {
  const identities = Array.from(new Set([
    transaction.beneficiaryName,
    includeBank ? transaction.beneficiaryBank : "",
    transaction.beneficiaryAccount,
    transaction.sourceAccount,
  ].filter((value): value is string => Boolean(value))));
  return identities.join(" · ") || unavailable;
}

export function TransactionPreview({ label, transaction, language, reasons, text, onView, referenceConflict = false, beneficiaryBankMatch, ftNumberMatch }: { label?: string; transaction: Transaction; language: Language; reasons: DuplicateReason[]; text: ReviewText; onView: () => void; referenceConflict?: boolean; beneficiaryBankMatch?: "same" | "different" | "missing"; ftNumberMatch?: "same" | "different" | "missing" }) {
  const direction = transaction.direction === "CREDIT" ? (language === "id" ? "Kredit" : "Credit") : (language === "id" ? "Debet" : "Debit");
  const dateTime = (transaction.transactionTimestamp || transaction.date).replace("T", " ");
  return <div className="min-w-0 rounded-xl bg-muted/25 p-3 sm:p-4">
    <div className="mb-3 flex items-center justify-between gap-2">{label ? <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p> : <span />}<Button type="button" size="sm" variant="ghost" className="h-8 text-[#82305b] dark:text-[#f1b9d5]" onClick={onView}><Eye />{text.viewDetails}</Button></div>
    <div className="space-y-2">
      <MatchField label={text.reference} value={transaction.paymentRef || text.unavailable} matched={reasons.includes("reference")} conflicted={referenceConflict} mono />
      {text.ftNumber && <MatchField label={text.ftNumber} value={transaction.ftNumber?.trim() || text.unavailable} matched={ftNumberMatch === "same"} conflicted={ftNumberMatch === "different"} mono />}
      <MatchField label={text.senderField} value={transaction.sourceName || text.unavailable} matched={reasons.includes("sender")} />
      <MatchField label={text.dateTime} value={dateTime} matched={reasons.includes("dateTime")} />
      <MatchField label={text.descriptionField} value={transaction.subject} matched={reasons.includes("description")} />
      <MatchField label={text.amount} value={money(transaction.amount, language)} matched={reasons.includes("amount")} />
      <MatchField label={text.direction} value={direction} matched={reasons.includes("direction")} />
      {text.beneficiaryBank && <MatchField label={text.beneficiaryBank} value={transaction.beneficiaryBank?.trim() || text.unavailable} matched={beneficiaryBankMatch === "same"} conflicted={beneficiaryBankMatch === "different"} />}
      <MatchField label={text.accountField} value={accountValue(transaction, text.unavailable, !text.beneficiaryBank)} matched={reasons.includes("account")} />
    </div>
  </div>;
}

function MatchField({ label, value, matched, mono = false, conflicted = false }: { label: string; value: string; matched: boolean; mono?: boolean; conflicted?: boolean }) {
  return <div className={`rounded-lg px-2.5 py-2 ${conflicted ? "bg-red-500/10 text-red-800 ring-1 ring-inset ring-red-500/40 dark:text-red-200" : matched ? "bg-amber-500/12 text-amber-950 ring-1 ring-inset ring-amber-500/25 dark:text-amber-100" : "bg-background/55"}`}>
    <p className={`text-[11px] font-semibold uppercase tracking-wider ${matched ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground"}`}>{label}</p>
    <p className={`mt-1 break-words text-sm ${matched ? "font-semibold" : ""} ${mono ? "font-mono" : ""}`}>{value}</p>
  </div>;
}
